import { useLocalStorage } from '@mantine/hooks';

import type { UiKeysPromoter } from '../../../types/UiKeys';
import { useLanguagesItemsQuery } from '../data/languages/useLanguagesItemsQuery.ts';
import { useContent } from './data/useContent.ts';
import { useTranslations } from './data/useTranslations.ts';

const replacePlaceholders = (template: string, values: Record<string, string>) => {
  return template.replaceAll(/\{(\w+)\}/g, (match: string, key: string) => {
    return key in values ? values[key] : match;
  });
};

export const useI18n = () => {
  const { isPending: languagesIsPending, data: languages } = useLanguagesItemsQuery();

  const [localStorageLocale, setLocalStorageLocale] = useLocalStorage<string | null>({
    key: 'active-locale',
    defaultValue: null,
  });

  const firstAvailableLocale = languages?.[0]?.code ?? null;

  const activeLocale = localStorageLocale ?? firstAvailableLocale;

  const { isPending: contentIsPending, data: content } = useContent(activeLocale);
  const { isPending: translationsIsPending, data: translations } = useTranslations(activeLocale);

  const t = (key: UiKeysPromoter, param?: Record<string, string>) => {
    const value = translations?.promoter?.[key];

    if (!value || !param) {
      return value;
    }

    return replacePlaceholders(value, param);
  };

  return {
    isPending: languagesIsPending || contentIsPending || translationsIsPending,
    availableLanguages: languages,
    setActiveLocale: setLocalStorageLocale,
    activeLocale: activeLocale ?? firstAvailableLocale,
    content,
    t,
  };
};
